import { useState, useEffect } from 'react'
import { teacherCodeApi, TeacherCodePattern } from '@/services/api/teacherCode'

export default function TeacherCodeManagementPage() {
  const [activeTab, setActiveTab] = useState<'pattern' | 'assignment'>('pattern')
  const [patterns, setPatterns] = useState<TeacherCodePattern[]>([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<{ total_teachers: number; with_code: number; without_code: number; active_pattern: { id: string; name: string; syntax: string } | null; auto_inferred_pattern: { pattern_syntax: string; confidence: number; matched_count: number; total_count: number; examples: Array<{ name: string; code: string }> } | null } | null>(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [patternsRes, statsRes] = await Promise.all([
        teacherCodeApi.listPatterns(),
        teacherCodeApi.getTeacherCodeStats(),
      ])
      setPatterns(patternsRes)
      setStats(statsRes)
    } catch (err) {
      console.error('Failed to load data:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Lehrer-Codes</h1>
        <p className="text-gray-600 mt-1">
          Muster verwalten und Lehrer-Codes zuweisen
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Gesamt</div>
            <div className="text-2xl font-bold">{stats.total_teachers}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Mit Code</div>
            <div className="text-2xl font-bold text-green-600">{stats.with_code}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Ohne Code</div>
            <div className="text-2xl font-bold text-orange-600">{stats.without_code}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Aktives Pattern</div>
            <div className="text-sm font-mono truncate">
              {stats.active_pattern?.syntax || 'Keines'}
            </div>
          </div>
        </div>
      )}

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('pattern')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'pattern'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Pattern-Management
          </button>
          <button
            onClick={() => setActiveTab('assignment')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'assignment'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Code-Zuweisung
          </button>
        </nav>
      </div>

      {loading ? (
        <div className="text-center py-8">Laden...</div>
      ) : activeTab === 'pattern' ? (
        <PatternTab patterns={patterns} onReload={loadData} />
      ) : (
        <AssignmentTab onReload={loadData} />
      )}
    </div>
  )
}

function PatternTab({ patterns, onReload }: { patterns: TeacherCodePattern[]; onReload: () => void }) {
  const [examples, setExamples] = useState([
    { name: '', code: '' },
    { name: '', code: '' },
    { name: '', code: '' },
  ])
  const [customSyntax, setCustomSyntax] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [inferLoading, setInferLoading] = useState(false)
  const [inferResult, setInferResult] = useState<{ pattern_syntax: string; confidence: number; matched_count: number; total_count: number; examples: Array<{ name: string; code: string }> } | null>(null)

  const handleInfer = async () => {
    setInferLoading(true)
    try {
      const res = await teacherCodeApi.autoInferPattern()
      if (res.pattern) {
        setInferResult(res.pattern as any)
        setMessage(res.message as string)
      } else {
        setMessage(res.message as string)
      }
    } catch (err) {
      setMessage('Auto-Inferenz fehlgeschlagen')
    } finally {
      setInferLoading(false)
    }
  }

  const handleApplyInferred = async () => {
    if (!inferResult) return
    setSaving(true)
    try {
      const validExamples = examples.filter(e => e.name && e.code) as Array<{ name: string; code: string }>
      const examplesToUse = validExamples.length >= 3 ? validExamples : (inferResult.examples || [])
      await teacherCodeApi.createPattern({
        examples: examplesToUse,
        custom_syntax: inferResult.pattern_syntax,
      })
      setMessage('Pattern übernommen')
      onReload()
    } catch (err) {
      setMessage('Fehler beim Erstellen des Patterns')
    } finally {
      setSaving(false)
    }
  }

  const handleSubmit = async () => {
    const validExamples = examples.filter(e => e.name && e.code)
    if (validExamples.length < 3) {
      setMessage('Mindestens 3 Beispiele erforderlich')
      return
    }
    setSaving(true)
    try {
      await teacherCodeApi.createPattern({
        examples: validExamples,
        custom_syntax: customSyntax || undefined,
      })
      setMessage('Pattern erstellt')
      setExamples([{ name: '', code: '' }, { name: '', code: '' }, { name: '', code: '' }])
      setCustomSyntax('')
      onReload()
    } catch (err: any) {
      setMessage(err.response?.data?.detail || 'Fehler beim Erstellen')
    } finally {
      setSaving(false)
    }
  }

  const updateExample = (idx: number, field: 'name' | 'code', value: string) => {
    const newExamples = [...examples]
    const ex = newExamples[idx]
    newExamples[idx] = { name: ex?.name ?? '', code: ex?.code ?? '' }
    newExamples[idx][field] = value
    setExamples(newExamples)
  }

  return (
    <div className="space-y-6">
      {message && (
        <div className={`p-4 rounded ${message.includes('Fehler') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          {message}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Auto-Inferenz</h2>
        <p className="text-sm text-gray-600 mb-4">
          Pattern automatisch aus bestehenden Lehrer-Daten inferieren.
        </p>
        <button
          onClick={handleInfer}
          disabled={inferLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {inferLoading ? 'Inferieren...' : 'Pattern inferieren'}
        </button>
        {inferResult && (
          <div className="mt-4 p-4 bg-gray-50 rounded">
            <div className="font-mono text-sm">{inferResult.pattern_syntax}</div>
            <div className="text-sm text-gray-600 mt-1">
              {inferResult.matched_count}/{inferResult.total_count} Treffer ({Math.round(inferResult.confidence * 100)}%)
            </div>
            <button
              onClick={handleApplyInferred}
              disabled={saving}
              className="mt-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
            >
              Übernehmen
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Neues Pattern erstellen</h2>
        <div className="space-y-3">
          {examples.map((ex, idx) => (
            <div key={idx} className="flex gap-3">
              <input
                type="text"
                placeholder={`Name (z.B. Marc Wolf)`}
                value={ex.name}
                onChange={e => updateExample(idx, 'name', e.target.value)}
                className="flex-1 px-3 py-2 border rounded"
              />
              <input
                type="text"
                placeholder={`Code (z.B. WOM)`}
                value={ex.code}
                onChange={e => updateExample(idx, 'code', e.target.value.toUpperCase())}
                className="w-24 px-3 py-2 border rounded"
              />
            </div>
          ))}
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Bracket-Syntax (optional)
          </label>
          <input
            type="text"
            placeholder="z.B. (W O) )L F( (M) )A R C("
            value={customSyntax}
            onChange={e => setCustomSyntax(e.target.value)}
            className="w-full px-3 py-2 border rounded font-mono"
          />
        </div>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          Pattern lernen
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Bestehende Patterns</h2>
        {patterns.length === 0 ? (
          <p className="text-gray-500">Keine Patterns vorhanden</p>
        ) : (
          <div className="space-y-2">
            {patterns.map(p => (
              <div key={p.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <div>
                  <span className="font-mono text-sm">{p.pattern_syntax}</span>
                  <span className="ml-2 text-sm text-gray-500">{p.pattern_name}</span>
                </div>
                {p.is_active && (
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Aktiv</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function AssignmentTab({ onReload }: { onReload: () => void }) {
  const [ruleType, setRuleType] = useState('first3_last')
  const [preview, setPreview] = useState<{ suggestions: Array<{ teacher_id: string; teacher_name: string; code: string }>; conflicts: Array<{ teacher_id: string; code: string; reason: string }> } | null>(null)
  const [loading, setLoading] = useState(false)
  const [applying, setApplying] = useState(false)
  const [message, setMessage] = useState('')

  const handlePreview = async () => {
    setLoading(true)
    try {
      const res = await teacherCodeApi.previewCodes(ruleType)
      setPreview(res)
    } catch (err) {
      setMessage('Vorschau fehlgeschlagen')
    } finally {
      setLoading(false)
    }
  }

  const handleApply = async () => {
    if (!preview || preview.suggestions.length === 0) return
    setApplying(true)
    try {
      await teacherCodeApi.applyCodes(preview.suggestions.map(s => ({ teacher_id: s.teacher_id, code: s.code })))
      setMessage(`${preview.suggestions.length} Codes zugewiesen`)
      setPreview(null)
      onReload()
    } catch (err) {
      setMessage('Fehler beim Zuweisen')
    } finally {
      setApplying(false)
    }
  }

  const handleApplyRule = async () => {
    setApplying(true)
    try {
      const res = await teacherCodeApi.applyRule(ruleType, false)
      setMessage(`${res.applied} Codes zugewiesen, ${res.conflicts_skipped} Konflikte übersprungen`)
      onReload()
    } catch (err) {
      setMessage('Fehler beim Anwenden der Regel')
    } finally {
      setApplying(false)
    }
  }

  return (
    <div className="space-y-6">
      {message && (
        <div className={`p-4 rounded ${message.includes('Fehler') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          {message}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Regel auswählen</h2>
        <select
          value={ruleType}
          onChange={e => setRuleType(e.target.value)}
          className="px-3 py-2 border rounded"
        >
          <option value="first3_last">Erste 3 Buchstaben Nachname</option>
          <option value="first_last2">Erster Vorname + letzte 2 Nachname</option>
          <option value="first2_last2">Erste 2 + letzte 2 Nachname</option>
          <option value="first_last1">Erster Vorname + letzter Nachname</option>
          <option value="pattern">Aktives Pattern</option>
        </select>
        <div className="flex gap-3 mt-4">
          <button
            onClick={handlePreview}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Laden...' : 'Vorschau generieren'}
          </button>
          <button
            onClick={handleApplyRule}
            disabled={applying}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          >
            {applying ? 'Anwenden...' : 'Direkt anwenden'}
          </button>
        </div>
      </div>

      {preview && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Vorschau ({preview.suggestions.length} Vorschläge, {preview.conflicts.length} Konflikte)</h2>
          <div className="max-h-96 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {preview.suggestions.map(s => (
                  <tr key={s.teacher_id}>
                    <td className="px-4 py-2">{s.teacher_name}</td>
                    <td className="px-4 py-2 font-mono">{s.code}</td>
                    <td className="px-4 py-2 text-green-600">OK</td>
                  </tr>
                ))}
                {preview.conflicts.map(c => (
                  <tr key={c.teacher_id} className="bg-red-50">
                    <td className="px-4 py-2">{c.teacher_id}</td>
                    <td className="px-4 py-2 font-mono">{c.code}</td>
                    <td className="px-4 py-2 text-red-600">{c.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            onClick={handleApply}
            disabled={applying || preview.suggestions.length === 0}
            className="mt-4 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          >
            {applying ? 'Anwenden...' : 'Alle anwenden'}
          </button>
        </div>
      )}
    </div>
  )
}
